<template>
  <div id="sidebar_wrapper">
    <UserModal
      v-model="inviteTeamModalVisible"
      :roles="roles"
      :users="users"
      :title="title"
      :subTitle="subTitle"
      :user-count="userCount"
      :is-editing="false"
    />

    <ScanToDownloadAppModal v-model:visible="installMobileModalVisible" />
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from "vue";
import { message } from "ant-design-vue";
import { sidebarOptionEventIDs } from "@/src/constants/sidebarConstants.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import BillingService from "@/src/api/BillingService.js";
import DashboardService from "@/src/api/DashboardService.js";
import AuthPermissionService from "@/src/api/AuthPermissionService.js";
import { isABACEnabled } from "@/src/utils/dynamicFormUtils.js";
import UserModal from "@/src/views/users/UserModal.vue";
import ScanToDownloadAppModal from "../ScanToDownloadAppModal.vue";

const inviteTeamModalVisible = ref(false);
const installMobileModalVisible = ref(false);
const roles = ref([]);
const users = ref([]);
const title = ref("Invite Your Team");
const subTitle = ref("Bring your team in—let SuperAGI handle the busywork");
const userCount = ref(0);

const eventHandlers = {
  [sidebarOptionEventIDs.INVITE_TEAM_MEMBER]: () => {
    inviteTeamModalVisible.value = true;
  },
  [sidebarOptionEventIDs.INSTALL_MOBILE_APP]: () => {
    installMobileModalVisible.value = true;
  },
};

const handleSidebarEvent = (eventId) => {
  try {
    if (eventHandlers[eventId]) {
      eventHandlers[eventId]();
    } else {
      console.log(`No handler for event: ${eventId}`);
    }
  } catch (error) {
    console.error("An error occurred while handling sidebar event: ", error);
  }
};

const fetchRoles = async () => {
  try {
    let response;
    
    if (isABACEnabled()) {
      response = await AuthPermissionService.fetchRoles();
      if (response.data) {
        roles.value = response.data.map((role) => ({
          id: role.id,
          role_name: role.attributes?.name,
        }));
      }
    } else {
      response = await DashboardService.fetchRoles();
      if (response.success) {
        roles.value = response.roles;
      }
    }
  } catch (error) {
    message.error("Failed to fetch roles");
  }
};

const fetchUsers = async () => {
  try {
    const response = await DashboardService.fetchUsers();
    const pendingResponse = await DashboardService.fetchPendingUsers();
    let pendingInviteCount = 0;
    if (response?.success && pendingResponse?.success) {
      pendingInviteCount = pendingResponse?.users?.filter(
        (user) => user.pending_invite,
      ).length;
      userCount.value = response.total_active + pendingInviteCount;
      users.value = response.users;
    }
  } catch (error) {
    console.error("Error fetching users:", error);
  }
};

const loadBillingData = async () => {
  try {
    // Fetch billing account
    const accountResponse = await BillingService.getBillingAccount();
    if (accountResponse?.success) {
      const hasPaidPlan = accountResponse.account?.has_paid_plan || false;
      store.commit("billing/SET_HAS_PAID_PLAN", hasPaidPlan);
    }

    // Fetch billing version
    const versionResponse =
      await DashboardService.fetchCurrentWorkspaceBillingVersion();
    if (versionResponse?.success) {
      store.commit(
        "billing/SET_BILLING_VERSION",
        versionResponse.billing_version,
      );
    }

    // Fetch total seats
    const seatsResponse = await BillingService.getTotalSeats();
    if (seatsResponse?.success) {
      store.commit("billing/SET_HUMAN_SEATS", seatsResponse.human_seats);
    }
  } catch (error) {
    console.error("Error loading billing data:", error);
  }
};

const hasEventHandler = (eventId) => {
  return !!eventHandlers[eventId];
};

watch(
  () => inviteTeamModalVisible.value,
  (newValue) => {
    if (newValue) {
      fetchUsers();
      loadBillingData();
    }
  },
);

onMounted(async () => {
  await fetchRoles();
  await loadBillingData();

  eventBus.on("user-altered", async () => {
    await fetchUsers();
    await loadBillingData();
  });
});

defineExpose({
  handleSidebarEvent,
  hasEventHandler,
});
</script>
